import axios from "axios";
import moment from "moment";
import { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { ExportIcon } from "../icons";
import { REPORTS_API_URL } from "../constants/Globals";
import { formatNumberWithCommas } from "../helpers/Commas";
import InfiniteScroll from "react-infinite-scroll-component";

const ChevronDownIcon = () => (
  <svg
    width="10"
    height="6"
    viewBox="0 0 10 6"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.63083 0.815397L4.81543 5.63079L3.26305e-05 0.815397H9.63083Z"
      fill="#828282"
    />
  </svg>
);

const ChevronUpIcon = () => (
  <svg
    width="10"
    height="6"
    viewBox="0 0 10 6"
    fill="none"
    style={{ transform: "scaleY(-1)" }}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.63083 0.815397L4.81543 5.63079L3.26305e-05 0.815397H9.63083Z"
      fill="#828282"
    />
  </svg>
);

export default function TableBox({
  tableData = [],
  columnTypes = [],
  columnTitles = [],
  additionColumnTypes = [],
  exportLink = null,
  cellTag = () => {},
  hasMoreData = false,
  ageKey = "stats-client-embed-domain-summary",
  sortDirection = "desc",
  sortByColumn = "",
  onSortChange,
  tableRowsTotal = {},
  onBackClick,
  onLoadMore,
  slugify,
  name = "",
  headerRequired = true,
}) {
  const getFromLocalStorage = (key, defaultValue) => {
    const saved = localStorage.getItem(name + "-" + key);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.warn(
          `Error parsing localStorage key "${key}". Returning default value.`
        );
        return defaultValue;
      }
    }
    return defaultValue;
  };

  const setInLocalStorage = (key, value) => {
    localStorage.setItem(name + "-" + key, JSON.stringify(value));
  };

  const getColumnTypeName = (slug) =>
    [...columnTypes, ...additionColumnTypes].find(
      (columnType) => slugify(columnType) === slug
    ) || "";

  const [lastUpdated, setLastUpdated] = useState(null);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [temporarySelectedColumnTypes, setTemporarySelectedColumnTypes] =
    useState([]);
  const [selectedColumnTypes, setSelectedColumnTypes] = useState(
    getFromLocalStorage(
      "selectedColumnTypes",
      columnTypes.map((type) => slugify(type))
    )
  );
  useEffect(() => {
    axios
      .get(`${REPORTS_API_URL}/reports/last-updated/${ageKey}`)
      .then(({ data }) => setLastUpdated(data.data));
  }, [ageKey]);

  useEffect(() => {
    setSelectedColumnTypes(
      getFromLocalStorage(
        "selectedColumnTypes",
        columnTypes.map((type) => slugify(type))
      )
    );
  }, [columnTypes]);

  const moneyColumns = [
    "revenue",
    "cpm",
    "arDueFromAdvertiser",
    "sidedNetRevenue",
    "apDueToCustomer",
  ];
  const percentColumns = [
    "customerShare",
    "netPercentage",
    "contactConversionRate",
    "ctaButtonClickRate",
    "engagementRate",
    "voteRate",
    "audiencePercent",
    "changePercent",
  ];
  const largeNumberColumns = [
    "adImpressions",
    "revenue",
    "contacts",
    "votes",
    "cpm",
    "engagements",
    "pollImpressions",
    "customerCount",
    "advertisers",
    "arDueFromAdvertiser",
    "apDueToCustomer",
    "netPercentage",
    "sidedNetRevenue",
    "contactConversionRate",
    "ctaButtonClicks",
    "ctaButtonClickRate",
    "engagementRate",
    "voteRate",
    "pollViews",
    "impressions",
  ];

  const hideHeaderTotalColumns = [
    "domain",
    "clientName",
    "name",
    "thesis",
    "customerCount",
    "advertisers",
    "date",
    "yearMonth",
    "source",
    "tag",
    "type",
    "audiencePercent",
    "changePercent",
  ];
  return (
    <>
      <div className="table-box">
        {headerRequired && (
          <div className="table-box-header">
            <div className="table-box-title">
              {onBackClick ? (
                <svg
                  width="20"
                  height="22"
                  viewBox="0 0 20 22"
                  fill="none"
                  className="text-button"
                  onClick={() => onBackClick()}
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_22422_2419)">
                    <path
                      d="M0.200517 10.5145C-0.0642348 10.7809 -0.0642348 11.2191 0.200517 11.4855L7.71604 19.048C7.9808 19.3145 8.41636 19.3145 8.68111 19.048C8.94586 18.7816 8.94586 18.3434 8.68111 18.077L2.33134 11.6875H18.447C18.8228 11.6875 19.1303 11.3781 19.1303 11C19.1303 10.6219 18.8228 10.3125 18.447 10.3125H2.33134L8.68111 3.92305C8.94586 3.65664 8.94586 3.21836 8.68111 2.95196C8.41636 2.68555 7.9808 2.68555 7.71604 2.95196L0.200517 10.5145Z"
                      fill="#333333"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_22422_2419">
                      <rect width="19.1304" height="22" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              ) : null}
              <svg
                width="17"
                height="17"
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.5 15.3C10.3035 15.3 12.0331 14.5836 13.3083 13.3083C14.5836 12.0331 15.3 10.3035 15.3 8.5C15.3 6.69653 14.5836 4.96692 13.3083 3.69167C12.0331 2.41643 10.3035 1.7 8.5 1.7C6.69653 1.7 4.96692 2.41643 3.69167 3.69167C2.41643 4.96692 1.7 6.69653 1.7 8.5C1.7 10.3035 2.41643 12.0331 3.69167 13.3083C4.96692 14.5836 6.69653 15.3 8.5 15.3ZM8.5 0C9.61624 0 10.7215 0.219859 11.7528 0.647024C12.7841 1.07419 13.7211 1.70029 14.5104 2.48959C15.2997 3.27889 15.9258 4.21592 16.353 5.24719C16.7801 6.27846 17 7.38376 17 8.5C17 10.7543 16.1045 12.9163 14.5104 14.5104C12.9163 16.1045 10.7543 17 8.5 17C3.7995 17 0 13.175 0 8.5C0 6.24566 0.895533 4.08365 2.48959 2.48959C4.08365 0.895533 6.24566 0 8.5 0ZM8.925 4.25V8.7125L12.75 10.982L12.1125 12.0275L7.65 9.35V4.25H8.925Z"
                  fill="#BDBDBD"
                />
              </svg>
              {lastUpdated ? (
                <span>
                  Data updated {moment().diff(moment(lastUpdated), "hours")}{" "}
                  hours ago
                </span>
              ) : null}
            </div>
            <div className="table-box-actions">
              {exportLink ? (
                <>
                  <span
                    className="text-button inherit-font"
                    onClick={() =>
                      axios
                        .get(`${exportLink}&export=1`)
                        .then(({ data }) => window.open(data.data, "_blank"))
                    }
                  >
                    <ExportIcon />
                    <span>Export</span>
                  </span>
                  <span className="separator"></span>
                </>
              ) : null}
              <span
                className="text-button inherit-font"
                onClick={() => {
                  setTemporarySelectedColumnTypes(
                    selectedColumnTypes.map((columnType) => columnType)
                  );
                  setShowCustomizeModal(true);
                }}
              >
                <svg
                  width="18"
                  height="12"
                  viewBox="0 0 18 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 7H15V5H3M0 0V2H18V0M7 12H11V10H7V12Z"
                    fill="#2F80ED"
                  />
                </svg>
                <span>Customize</span>
              </span>
            </div>
          </div>
        )}
        <div className="table-box-content">
          <InfiniteScroll
            dataLength={tableData.length} // Length of data loaded so far
            next={onLoadMore} // Function to fetch more data
            hasMore={hasMoreData} // Boolean to indicate if more data can be loaded
            loader={
              <h6 className="txtCenter infinite-loading">Loading more...</h6>
            } // Loader to display while fetching data
          >
            <table cellSpacing="0" cellPadding="0">
              <tbody>
                <tr>
                  {selectedColumnTypes.map((selectedColumnType, index) => (
                    <th
                      key={selectedColumnType}
                      onClick={() => {
                        if (onSortChange) {
                          onSortChange(
                            selectedColumnType,
                            selectedColumnType === sortByColumn &&
                              sortDirection === "desc"
                              ? "asc"
                              : "desc"
                          );
                        }
                      }}
                    >
                      {getColumnTypeName(selectedColumnType)}
                      {sortByColumn === selectedColumnType ? (
                        <>
                          {sortDirection === "desc" ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )}
                        </>
                      ) : null}
                    </th>
                  ))}
                </tr>

                <tr className="totals-row">
                  {selectedColumnTypes.map((selectedColumnType, index) => (
                    <td className="txtLeft" key={selectedColumnType}>
                      {moneyColumns.includes(selectedColumnType) ? "$" : ""}
                      {!hideHeaderTotalColumns.includes(selectedColumnType) &&
                        formatNumberWithCommas(
                          tableRowsTotal[selectedColumnType] || 0
                        )}
                      {percentColumns.includes(selectedColumnType) ? "%" : ""}
                    </td>
                  ))}
                </tr>
                {tableData.map((row, idx) => (
                  <tr key={`${row.date}_${idx}`}>
                    {selectedColumnTypes.map((selectedColumnType, index) => (
                      <th key={selectedColumnType}>
                        <span>
                          {cellTag(row, selectedColumnType) || (
                            <>
                              {moneyColumns.includes(selectedColumnType)
                                ? "$"
                                : ""}
                              {largeNumberColumns.includes(selectedColumnType)
                                ? formatNumberWithCommas(
                                    row[selectedColumnType]
                                  )
                                : row[selectedColumnType]}
                              {percentColumns.includes(selectedColumnType)
                                ? "%"
                                : ""}
                            </>
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </InfiniteScroll>
        </div>
      </div>

      <Modal size="lg" show={showCustomizeModal}>
        <Modal.Body className="sided-modal-body padding-s">
          <div className="sided-modal-title">Customize Columns</div>
          <div className="sided-modal-checkers">
            <div className="network-list-row">
              <label className="network-list-item network-list-name">
                <input
                  type="checkbox"
                  className="sided-checkbox"
                  checked={
                    temporarySelectedColumnTypes.length ===
                    [...columnTypes, ...additionColumnTypes].length
                  }
                  onChange={() =>
                    setTemporarySelectedColumnTypes(
                      [...columnTypes, ...additionColumnTypes].map(
                        (columnType) => slugify(columnType)
                      )
                    )
                  }
                />
                Select All
              </label>
              <div>{temporarySelectedColumnTypes.length} selected</div>
            </div>
            {[...columnTypes, ...additionColumnTypes].map((columnType) => (
              <div key={columnType} className="network-list-row">
                <label className="network-list-item network-list-name">
                  <input
                    type="checkbox"
                    className="sided-checkbox"
                    checked={temporarySelectedColumnTypes.includes(
                      slugify(columnType)
                    )}
                    onChange={() => {
                      const selection = [].concat(temporarySelectedColumnTypes);
                      const slug = slugify(columnType);

                      if (selection.includes(slug)) {
                        selection.splice(selection.indexOf(slug), 1);
                      } else {
                        selection.push(slug);
                      }

                      setTemporarySelectedColumnTypes(
                        [...columnTypes, ...additionColumnTypes]
                          .filter((columnType) =>
                            selection.includes(slugify(columnType))
                          )
                          .map((columnType) => slugify(columnType))
                      );
                    }}
                  />
                  <div className="network-list-logo">{columnType}</div>
                </label>
              </div>
            ))}
          </div>

          <div className="text-end">
            <Button
              className="mx-2"
              variant="secondary"
              onClick={() => setShowCustomizeModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setShowCustomizeModal(false);
                setSelectedColumnTypes(
                  temporarySelectedColumnTypes.map((columnType) => columnType)
                );
                setInLocalStorage(
                  "selectedColumnTypes",
                  temporarySelectedColumnTypes
                );
              }}
            >
              Apply
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
